import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import MenuDisplayPage from "./pages/MenuDisplayPage";
import LoginPage from "./pages/LoginPage";
import CustomerPage from "./pages/CustomerPage";
import BookingPage from "./pages/BookingPage";
import PrepPanel from "./pages/PrepPanel";
import BarPanel from "./pages/BarPanel";
import WaiterPanel from "./pages/WaiterPanel";
import KioskPage from "./pages/KioskPage";
import DirectorDashboard from "./pages/DirectorDashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import InstallPrompt from "./components/InstallPrompt";
import OfflineSync from "./components/OfflineSync";
import GlobalServiceBell from "./components/GlobalServiceBell";
import DJPanel from "./pages/DJPanel";
import POSPage from "./pages/POSPage";

// Dashboard Layout + Pages
import DashboardLayout from "./components/DashboardLayout";
import OverviewPage from "./pages/dashboard/OverviewPage";
import OrdersPage from "./pages/dashboard/OrdersPage";
import MenuPage from "./pages/dashboard/MenuPage";
import TablesPage from "./pages/dashboard/TablesPage";
import StaffPage from "./pages/dashboard/StaffPage";
import CustomersPage from "./pages/dashboard/CustomersPage";
import TechPage from "./pages/dashboard/TechPage";
import AuditPage from "./pages/dashboard/AuditPage";
import DiscountReportPage from "./pages/dashboard/DiscountReportPage";
import HardDeletePage from "./pages/dashboard/HardDeletePage";
import QuickLinksPage from "./pages/dashboard/QuickLinksPage";
import ReportsPage from "./pages/dashboard/ReportsPage";
import StaffManagementPage from "./pages/dashboard/StaffManagementPage";
import RosterPage from "./pages/dashboard/RosterPage";
import ClockPage from "./pages/dashboard/ClockPage";
import LeavePage from "./pages/dashboard/LeavePage";
import TimesheetsPage from "./pages/dashboard/TimesheetsPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import ReservationsPage from "./pages/dashboard/ReservationsPage";

// Standalone tab components used as pages
import InventoryTab from "./components/InventoryTab";
import TillTab from "./components/TillTab";
import SupplierTab from "./components/SupplierTab";
import PerformanceTab from "./components/PerformanceTab";
import FeedbackTab from "./components/FeedbackTab";
import DamageTab from "./components/DamageTab";
import PurchaseOrderTab from "./components/PurchaseOrderTab";
import LocationsTab from "./components/LocationsTab";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Already-authenticated user hitting a route they don't have access to:
  // bounce them to their own home instead of dumping them on /login (which
  // would force a re-auth flicker — and is wrong UX for e.g. a bartender
  // accidentally typing /dashboard/staff).
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Manager-tier admin routes — manager, director (+ legacy admin/owner). Used
// to gate menu/staff/insights/reservations/etc. from bartenders (who share
// the Live-Orders + Tables surface but not admin tooling).
const MANAGER_ROLES = ['manager', 'director', 'admin', 'owner'];
// Roles allowed to access the Purchase Orders page — managers + the 3
// staff roles that need to draft restocks for their own area (bar / kitchen
// / shisha). Backend further scopes each role to its inventory category.
const PO_ROLES = ['manager', 'director', 'admin', 'owner', 'bartender', 'chef', 'shisha_expert'];

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Mirror LoginPage's roleRoutes EXACTLY so the installed PWA (which always
  // launches from "/") lands prep staff on their own panel instead of a
  // dashboard they can't operate from. Bartender/chef/shisha expert MUST land
  // on /bar, /kitchen, /shisha respectively.
  // NOTE (May 31 2026): kiosk role + dedicated kiosk station login removed.
  // Staff now sign in under their own role and clock in/out from the
  // Clock page inside the regular dashboard.
  const roleRoutes = {
    director: '/dashboard',
    manager: '/dashboard',
    till_supervisor: '/pos', // Dedicated staff POS — replaces the customer
                              // QR menu that was previously shoehorned into
                              // the Bar Panel's "Take Order" tab. Touch-first
                              // tile grid + inline payments. Feb 2026.
    tech: '/dashboard',
    chef: '/kitchen',
    bartender: '/bar',
    shisha_expert: '/shisha',
    waiter: '/serving',
    dj: '/dj',
  };
  return <Navigate to={roleRoutes[user.role] || '/dashboard'} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          theme="dark"
          position="bottom-right"
          // Shorter default duration (1.8s) + cap stacked toasts at 3 so
          // bulk operations like rapidly creating items don't pile up a
          // tower of "Item created" confirmations on the menu page
          // (owner-reported May 31 2026, IMG_2711).
          duration={1800}
          visibleToasts={3}
          toastOptions={{
            style: { background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', color: '#F5F5F5' }
          }}
        />
        <GlobalServiceBell />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/kiosk" element={<KioskPage />} />
          <Route path="/table/:qrCode" element={<CustomerPage />} />
          <Route path="/takeaway" element={<CustomerPage />} />
          <Route path="/menu-display" element={<MenuDisplayPage />} />
          <Route path="/book" element={<BookingPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/dashboard" element={
            <ProtectedRoute roles={['manager', 'director', 'chef', 'bartender', 'shisha_expert', 'waiter', 'tech']}>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<OverviewPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="menu" element={<ProtectedRoute roles={MANAGER_ROLES}><MenuPage /></ProtectedRoute>} />
            <Route path="tables" element={<TablesPage />} />
            <Route path="inventory" element={<ProtectedRoute roles={MANAGER_ROLES}><InventoryTab /></ProtectedRoute>} />
            <Route path="purchase-orders" element={<ProtectedRoute roles={PO_ROLES}><PurchaseOrderTab /></ProtectedRoute>} />
            <Route path="suppliers" element={<ProtectedRoute roles={MANAGER_ROLES}><SupplierTab /></ProtectedRoute>} />
            <Route path="till" element={<ProtectedRoute roles={MANAGER_ROLES}><TillTab /></ProtectedRoute>} />
            <Route path="staff" element={<ProtectedRoute roles={MANAGER_ROLES}><StaffPage /></ProtectedRoute>} />
            <Route path="customers" element={<ProtectedRoute roles={MANAGER_ROLES}><CustomersPage /></ProtectedRoute>} />
            <Route path="reservations" element={<ProtectedRoute roles={MANAGER_ROLES}><ReservationsPage /></ProtectedRoute>} />
            <Route path="performance" element={<ProtectedRoute roles={MANAGER_ROLES}><PerformanceTab /></ProtectedRoute>} />
            <Route path="feedback" element={<ProtectedRoute roles={MANAGER_ROLES}><FeedbackTab /></ProtectedRoute>} />
            <Route path="damages" element={<ProtectedRoute roles={MANAGER_ROLES}><DamageTab /></ProtectedRoute>} />
            <Route path="analytics" element={<ProtectedRoute roles={MANAGER_ROLES}><DirectorDashboard /></ProtectedRoute>} />
            <Route path="audit" element={<ProtectedRoute roles={MANAGER_ROLES}><AuditPage /></ProtectedRoute>} />
            <Route path="discounts" element={<ProtectedRoute roles={MANAGER_ROLES}><DiscountReportPage /></ProtectedRoute>} />
            <Route path="hard-delete" element={<ProtectedRoute roles={['director', 'admin', 'owner']}><HardDeletePage /></ProtectedRoute>} />
            <Route path="locations" element={<ProtectedRoute roles={['director', 'admin', 'owner']}><LocationsTab /></ProtectedRoute>} />
            <Route path="quick-links" element={<QuickLinksPage />} />
            <Route path="reports" element={<ProtectedRoute roles={MANAGER_ROLES}><ReportsPage /></ProtectedRoute>} />
            <Route path="staff-management" element={<ProtectedRoute roles={MANAGER_ROLES}><StaffManagementPage /></ProtectedRoute>} />
            <Route path="roster" element={<RosterPage />} />
            <Route path="clock" element={<ClockPage />} />
            <Route path="leave" element={<LeavePage />} />
            <Route path="timesheets" element={<ProtectedRoute roles={MANAGER_ROLES}><TimesheetsPage /></ProtectedRoute>} />
            <Route path="settings" element={
              <ProtectedRoute roles={['manager', 'director']}><SettingsPage /></ProtectedRoute>
            } />
            <Route path="tech" element={
              <ProtectedRoute roles={['tech', 'director', 'admin', 'owner']}><TechPage /></ProtectedRoute>
            } />
          </Route>

          {/* Staff panels */}
          <Route path="/kitchen" element={
            <ProtectedRoute roles={['chef', 'manager', 'director']}><PrepPanel type="kitchen" title="Kitchen" /></ProtectedRoute>
          } />
          <Route path="/bar" element={
            <ProtectedRoute roles={['bartender', 'till_supervisor', 'manager', 'director']}><BarPanel /></ProtectedRoute>
          } />
          <Route path="/shisha" element={
            <ProtectedRoute roles={['shisha_expert', 'manager', 'director']}><PrepPanel type="shisha" title="Shisha" /></ProtectedRoute>
          } />
          {/* /serving is INTENTIONALLY public — Feb 2026 owner ask
              "everyone should be able to clock in / out from the
              serving panel". The KioskClockWidget inside the panel
              works without an authenticated session via the public
              /api/staff/clock/kiosk-action endpoint. The orders /
              floor view inside the panel render only when a user
              with the right role is signed in. */}
          <Route path="/serving" element={<WaiterPanel />} />
          <Route path="/dj" element={
            <ProtectedRoute roles={['dj', 'manager', 'director', 'admin', 'owner']}><DJPanel /></ProtectedRoute>
          } />
          <Route path="/pos" element={
            <ProtectedRoute roles={['till_supervisor', 'manager', 'director']}><POSPage /></ProtectedRoute>
          } />

          <Route path="/" element={<RoleRedirect />} />
          <Route path="/analytics" element={<Navigate to="/dashboard/analytics" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <InstallPrompt />
        <OfflineSync />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
